export const NUMISTRAIL_ID_PATTERN = /^[a-z0-9_]+$/;
export const CERT_NUMBER_PATTERN = /^[A-Za-z0-9\-/\s]+$/;
export const PHONE_PATTERN = /^\+?[1-9]\d{1,14}$/;
export const UNSAFE_TEXT_PATTERN = /[<>]/;
export const CONTROL_CHARS_PATTERN = /[\u0000-\u001F\u007F]/;
export const EMAIL_PATTERN = /.+@.+\..+/;

const MIN_NUMISTRAIL_ID_LEN = 3;
const MAX_NUMISTRAIL_ID_LEN = 64;
const MIN_PASSWORD_LEN = 12;
const MAX_EMAIL_LEN = 120;
const MAX_CERTIFICATION_NUMBER_LEN = 128;
const MAX_COUNTRY_LEN = 64;
const MAX_MINT_LEN = 64;
const MAX_DENOMINATION_LEN = 64;
const MAX_GRADE_LEN = 32;
const MAX_GRADE_TERM_LEN = 64;
const MAX_GRADE_ATTRIBUTE_TEXT_LEN = 128;
const MAX_COLLECTION_LEN = 255;
const MAX_METAL_LEN = 64;
const MAX_SLAB_TOTAL_WEIGHT_GRAMS = 1000000;
const MAX_DESCRIPTION_CHARS = 512;
const MAX_DESCRIPTION_BYTES = 1536;
const MAX_COMPANY_NAME_LEN = 255;
const MAX_COMPANY_ADDRESS_LEN = 512;
const MAX_COMPANY_WEBSITE_LEN = 255;
const MAX_COMPANY_COUNTRY_LEN = 128;
export const AVOIRDUPOIS_OUNCE_TO_GRAMS = 28.349523125;

export function normalizeInput(value, { lowercase = false, uppercase = false } = {}) {
  if (value === null || value === undefined) return "";
  let normalized = String(value).normalize("NFKC").trim();
  if (lowercase) normalized = normalized.toLowerCase();
  if (uppercase) normalized = normalized.toUpperCase();
  return normalized;
}

export function utf8ByteLength(value) {
  const normalized = normalizeInput(value);
  if (!normalized) return 0;
  if (typeof TextEncoder !== "undefined") {
    return new TextEncoder().encode(normalized).length;
  }
  return unescape(encodeURIComponent(normalized)).length;
}

function hasUnsafeChars(value) {
  return UNSAFE_TEXT_PATTERN.test(value) || CONTROL_CHARS_PATTERN.test(value);
}

export function validateNumistrailId(value) {
  const normalized = normalizeInput(value, { lowercase: true });
  if (!normalized) {
    return "validation.numisId.required";
  }
  if (normalized.length < MIN_NUMISTRAIL_ID_LEN || normalized.length > MAX_NUMISTRAIL_ID_LEN) {
    return "validation.numisId.length";
  }
  if (!NUMISTRAIL_ID_PATTERN.test(normalized)) {
    return "validation.numisId.characters";
  }
  return null;
}

export function validatePassword(value) {
  const normalized = normalizeInput(value);
  if (!normalized) {
    return "validation.password.required";
  }
  if (normalized.length < MIN_PASSWORD_LEN) {
    return "validation.password.length";
  }
  if (!/[a-z]/.test(normalized) || !/[A-Z]/.test(normalized) || !/\d/.test(normalized) || !/[^A-Za-z0-9]/.test(normalized)) {
    return "validation.password.complexity";
  }
  return null;
}

export function validateEmail(value) {
  const normalized = normalizeInput(value, { lowercase: true });
  if (!normalized) {
    return "validation.email.required";
  }
  if (normalized.length > MAX_EMAIL_LEN) {
    return "validation.email.length";
  }
  if (!EMAIL_PATTERN.test(normalized)) {
    return "validation.email.format";
  }
  return null;
}

export function validatePhone(value) {
  const normalized = normalizeInput(value);
  if (!normalized) {
    return null;
  }
  if (!PHONE_PATTERN.test(normalized)) {
    return "validation.phone.format";
  }
  return null;
}

export function validateCertificationNumber(value) {
  const normalized = normalizeInput(value);
  if (!normalized) {
    return "validation.certNumber.required";
  }
  if (normalized.length > MAX_CERTIFICATION_NUMBER_LEN) {
    return "validation.certNumber.length";
  }
  if (!CERT_NUMBER_PATTERN.test(normalized) || hasUnsafeChars(normalized)) {
    return "validation.certNumber.characters";
  }
  return null;
}

export function validateDescription(value) {
  const normalized = normalizeInput(value);
  if (!normalized) {
    return null;
  }
  if (normalized.length > MAX_DESCRIPTION_CHARS || utf8ByteLength(normalized) > MAX_DESCRIPTION_BYTES) {
    return "validation.description.lengthMax";
  }
  if (hasUnsafeChars(normalized)) {
    return "validation.text.characters";
  }
  return null;
}

export function validateCountry(value) {
  const normalized = normalizeInput(value);
  if (!normalized) {
    return "validation.country.required";
  }
  if (normalized.length > MAX_COUNTRY_LEN) {
    return "validation.country.length";
  }
  if (hasUnsafeChars(normalized)) {
    return "validation.text.characters";
  }
  return null;
}

export function validateMint(value) {
  const normalized = normalizeInput(value);
  if (!normalized) {
    return null;
  }
  if (normalized.length > MAX_MINT_LEN) {
    return "validation.mint.length";
  }
  if (hasUnsafeChars(normalized)) {
    return "validation.text.characters";
  }
  return null;
}

export function validateDenomination(value) {
  const normalized = normalizeInput(value);
  if (!normalized) {
    return "validation.denomination.required";
  }
  if (normalized.length > MAX_DENOMINATION_LEN) {
    return "validation.denomination.length";
  }
  if (hasUnsafeChars(normalized)) {
    return "validation.text.characters";
  }
  return null;
}

export function validateYear(value) {
  const normalized = normalizeInput(value);
  if (!normalized) {
    return "validation.year.required";
  }
  const year = Number(normalized);
  if (!Number.isInteger(year)) {
    return "validation.year.integer";
  }
  if (year < -3000 || year > 3000) {
    return "validation.year.range";
  }
  return null;
}

export function sanitizeYearInput(value) {
  const normalized = normalizeInput(value).replace(/\s+/g, "");
  if (!normalized) return "";

  const hasLeadingMinus = normalized.startsWith("-");
  const digitsOnly = normalized.replace(/\D/g, "");
  if (!digitsOnly) {
    return hasLeadingMinus ? "-" : "";
  }
  return `${hasLeadingMinus ? "-" : ""}${digitsOnly}`;
}

export function validateGrade(value) {
  const normalized = normalizeInput(value);
  if (!normalized) {
    return "validation.grade.required";
  }
  if (normalized.length > MAX_GRADE_LEN) {
    return "validation.grade.length";
  }
  if (hasUnsafeChars(normalized)) {
    return "validation.text.characters";
  }
  return null;
}

export const GRADING_SYSTEMS = ["sheldon", "ancient_descriptive"];
export const SHELDON_DETAILS_CODES = ["G", "VG", "F", "VF", "XF", "AU", "UNC"];

export function parseSheldonDetails(value) {
  const normalized = normalizeInput(value);
  const match = normalized.match(/^(G|VG|F|VF|XF|AU|UNC)\s+details$/i);
  if (!match) return null;
  const code = match[1].toUpperCase();
  return SHELDON_DETAILS_CODES.includes(code) ? `${code}_DETAILS` : null;
}

export function validateGradingSystem(value) {
  const normalized = normalizeInput(value, { lowercase: true });
  if (!normalized) {
    return "validation.grade.system.required";
  }
  if (!GRADING_SYSTEMS.includes(normalized)) {
    return "validation.grade.system.invalid";
  }
  return null;
}

export function extractSheldonNumeric(value) {
  const normalized = normalizeInput(value);
  const match = normalized.match(/(\d{1,3})/);
  if (!match) return null;
  const numeric = Number(match[1]);
  if (!Number.isInteger(numeric)) return null;
  if (numeric < 1 || numeric > 70) return null;
  return numeric;
}

export function validateSheldonGrade(value) {
  const base = validateGrade(value);
  if (base) return base;
  const detailsCode = parseSheldonDetails(value);
  if (detailsCode) return null;
  const numeric = extractSheldonNumeric(value);
  if (numeric === null) {
    return "validation.grade.sheldon.range";
  }
  return null;
}

export const GRADE_TERM_CODE_PATTERN = /^[A-Z0-9_]+$/;

export function validateGradeTermCode(value) {
  const normalized = normalizeInput(value, { uppercase: true });
  if (!normalized) {
    return "validation.grade.term.required";
  }
  if (normalized.length > MAX_GRADE_TERM_LEN) {
    return "validation.grade.term.length";
  }
  if (!GRADE_TERM_CODE_PATTERN.test(normalized)) {
    return "validation.grade.term.characters";
  }
  return null;
}

export function validateGradeAttributeIntOptional(value) {
  const normalized = normalizeInput(value);
  if (!normalized) {
    return null;
  }
  const numeric = Number(normalized);
  if (!Number.isInteger(numeric)) {
    return "validation.grade.attribute.int_required";
  }
  if (numeric < 1 || numeric > 5) {
    return "validation.grade.attribute.range_1_5";
  }
  return null;
}

export function validateGradeAttributeTextOptional(value) {
  const normalized = normalizeInput(value);
  if (!normalized) {
    return null;
  }
  if (normalized.length > MAX_GRADE_ATTRIBUTE_TEXT_LEN) {
    return "validation.grade.attribute.text_length";
  }
  if (hasUnsafeChars(normalized)) {
    return "validation.text.characters";
  }
  return null;
}

export function validateCollectionName(value) {
  const normalized = normalizeInput(value);
  if (!normalized) {
    return null;
  }
  if (normalized.length > MAX_COLLECTION_LEN) {
    return "validation.collection.length";
  }
  if (hasUnsafeChars(normalized)) {
    return "validation.text.characters";
  }
  return null;
}

export function validateCompanyName(value, fieldKey) {
  const normalized = normalizeInput(value);
  if (!normalized) {
    return `validation.company.${fieldKey}.required`;
  }
  if (normalized.length > MAX_COMPANY_NAME_LEN) {
    return `validation.company.${fieldKey}.length`;
  }
  if (hasUnsafeChars(normalized)) {
    return "validation.text.characters";
  }
  return null;
}

export function validateCompanyAddress(value, { required = false } = {}) {
  const normalized = normalizeInput(value);
  if (!normalized) {
    return required ? "validation.company.address.required" : null;
  }
  if (normalized.length > MAX_COMPANY_ADDRESS_LEN) {
    return "validation.company.address.length";
  }
  if (hasUnsafeChars(normalized)) {
    return "validation.text.characters";
  }
  return null;
}

export function validateCompanyWebsite(value) {
  const normalized = normalizeInput(value);
  if (!normalized) {
    return "validation.company.website.required";
  }
  if (normalized.length > MAX_COMPANY_WEBSITE_LEN) {
    return "validation.company.website.length";
  }
  if (hasUnsafeChars(normalized)) {
    return "validation.text.characters";
  }
  const candidate = normalized.includes("://") ? normalized : `https://${normalized}`;
  try {
    const parsed = new URL(candidate);
    if (!parsed.hostname || !parsed.hostname.includes(".")) {
      return "validation.company.website.format";
    }
  } catch {
    return "validation.company.website.format";
  }
  return null;
}

export function validateCompanyCountryOptional(value, { required = true } = {}) {
  const normalized = normalizeInput(value);
  if (!normalized) {
    return required ? "validation.company.country.required" : null;
  }
  if (normalized.length > MAX_COMPANY_COUNTRY_LEN) {
    return "validation.company.country.length";
  }
  if (hasUnsafeChars(normalized)) {
    return "validation.text.characters";
  }
  return null;
}

export function getWeightInputUnit(locale) {
  const normalizedLocale = String(locale || "").toLowerCase();
  return normalizedLocale.startsWith("en") ? "oz" : "g";
}

export function normalizeOptionalSlabWeightInput(value, locale) {
  return normalizeInput(value).replace(/\s+/g, "");
}

export function sanitizeSlabWeightInput(value, locale) {
  const normalized = normalizeOptionalSlabWeightInput(value, locale);
  if (!normalized) {
    return "";
  }
  const decimalSeparator = getWeightInputUnit(locale) === "oz" ? "." : ",";
  let result = "";
  let hasSeparator = false;
  let fractionDigits = 0;

  for (const char of normalized) {
    if (/\d/.test(char)) {
      if (!hasSeparator) {
        result += char;
      } else if (fractionDigits < 3) {
        result += char;
        fractionDigits += 1;
      }
      continue;
    }
    if (char === decimalSeparator && !hasSeparator && result) {
      result += char;
      hasSeparator = true;
    }
  }

  return result;
}

function parseNormalizedSlabWeightInput(value, locale) {
  const normalized = sanitizeSlabWeightInput(value, locale);
  if (!normalized) {
    return null;
  }
  const usesOunces = getWeightInputUnit(locale) === "oz";
  const pattern = usesOunces
    ? /^\d+(\.\d{1,3})?$/
    : /^\d+(,\d{1,3})?$/;
  if (!pattern.test(normalized)) {
    return { normalized, numeric: null };
  }
  const canonical = usesOunces ? normalized : normalized.replace(",", ".");
  const numeric = Number(canonical);
  return { normalized, numeric };
}

export function validateSlabTotalWeight(value, locale) {
  const parsed = parseNormalizedSlabWeightInput(value, locale);
  if (!parsed) {
    return null;
  }
  if (!Number.isFinite(parsed.numeric)) {
    return "validation.slabWeight.format";
  }
  if (parsed.numeric <= 0) {
    return "validation.slabWeight.range";
  }
  const grams = getWeightInputUnit(locale) === "oz"
    ? parsed.numeric * AVOIRDUPOIS_OUNCE_TO_GRAMS
    : parsed.numeric;
  if (!Number.isFinite(grams) || grams > MAX_SLAB_TOTAL_WEIGHT_GRAMS) {
    return "validation.slabWeight.range";
  }
  return null;
}

export function parseSlabTotalWeightToGrams(value, locale) {
  const parsed = parseNormalizedSlabWeightInput(value, locale);
  if (!parsed || !Number.isFinite(parsed.numeric) || parsed.numeric <= 0) {
    return null;
  }
  const grams = getWeightInputUnit(locale) === "oz"
    ? parsed.numeric * AVOIRDUPOIS_OUNCE_TO_GRAMS
    : parsed.numeric;
  if (!Number.isFinite(grams)) {
    return null;
  }
  return Number(grams.toFixed(3));
}

export function formatSlabTotalWeightInputFromGrams(grams, locale) {
  if (grams === null || grams === undefined || grams === "") {
    return "";
  }
  const numeric = Number(grams);
  if (!Number.isFinite(numeric) || numeric <= 0) {
    return "";
  }
  const displayValue = getWeightInputUnit(locale) === "oz"
    ? numeric / AVOIRDUPOIS_OUNCE_TO_GRAMS
    : numeric;
  const fixed = displayValue.toFixed(3);
  return getWeightInputUnit(locale) === "g"
    ? fixed.replace(".", ",")
    : fixed;
}

export const METAL_SUGGESTION_KEYS = [
  "gold",
  "silver",
  "copper",
  "bronze",
  "bimetallic",
  "cupronickel",
];

export function normalizeOptionalMetal(value) {
  return normalizeInput(value).replace(/\s+/g, " ");
}

export function validateMetal(value) {
  const normalized = normalizeOptionalMetal(value);
  if (!normalized) {
    return null;
  }
  if (normalized.length > MAX_METAL_LEN) {
    return "validation.metal.length";
  }
  if (hasUnsafeChars(normalized)) {
    return "validation.text.characters";
  }
  return null;
}
